import { useCallback, useEffect, useState } from 'react';

import { Button, Colors, Flex, Text, DownloadLine } from '@revenuemarket/cds';
import { mixpanelTrack } from '@revenuemarket/utils/mixpanel';
import Image from 'next/image';

import useCertificate from '~hooks/useCertificate';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

const CodefDownloadSection = ({
  setDownloadNeeded,
}: {
  setDownloadNeeded: (downloadNeeded: boolean) => void;
}) => {
  const [downloadClicked, setDownloadClicked] = useState(false);
  const [isRegisterButtonDisabled, setIsRegisterButtonDisabled] = useState(false);
  const [codefNotInstalled, setCodefNotInstalled] = useState(false);
  const { isVerifying, downloadCodefProgram, checkCodefInstalled } = useCertificate();

  useEffect(() => {
    if (downloadClicked) {
      setIsRegisterButtonDisabled(true);
      const timer = setTimeout(() => {
        setIsRegisterButtonDisabled(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [downloadClicked]);

  const handleOpenCertificateRegistration = useCallback(async () => {
    mixpanelTrack(MIXPANEL_EVENT.CERTIFICATE.CLICK_ADD_CERTIFICATE);
    const { downloadNeeded } = await checkCodefInstalled();
    if (downloadNeeded) {
      setCodefNotInstalled(true);
    } else {
      setDownloadNeeded(false);
    }
  }, [checkCodefInstalled, setDownloadNeeded]);

  return (
    <Flex
      direction="column"
      gap={16}
      style={{ width: '100%', paddingTop: 24 }}
      align="center"
      justify="center"
    >
      <Image
        src="https://static.clobe.ai/image/codef-monitor-img.png"
        alt="codef-monitor-img"
        width={100}
        height={100}
        objectFit="contain"
      />
      {downloadClicked ? (
        <Text textStyle="18_600" style={{ color: Colors.gray900, textAlign: 'center' }}>
          {codefNotInstalled ? (
            <>
              프로그램을 찾을 수 없어요
              <br />
              프로그램을 설치하고 공동인증서를 등록해주세요
            </>
          ) : (
            '프로그램을 설치하고 공동인증서를 연동을 시작하세요'
          )}
        </Text>
      ) : (
        <>
          <Text textStyle="18_600" style={{ color: Colors.gray900 }}>
            공동인증서 연동을 위한 프로그램 설치가 필요합니다
          </Text>
          <Text textStyle="14_400" style={{ color: Colors.gray900 }}>
            은행과 홈택스에서 데이터를 안전하게 불러오기 위해 사용됩니다.
          </Text>
        </>
      )}
      {downloadClicked ? (
        codefNotInstalled ? (
          <Flex align="center" gap={8} style={{ width: '100%' }}>
            <Button
              icon={<DownloadLine fill={Colors.gray900} />}
              buttonSize="xlarge"
              onClick={() => {
                downloadCodefProgram();
              }}
            >
              다시 다운로드
            </Button>
            <Button
              icon={downloadClicked ? null : <DownloadLine />}
              buttonSize="xlarge"
              buttonType="primary"
              disabled={isRegisterButtonDisabled}
              style={{
                flex: 1,
              }}
              loading={isVerifying}
              onClick={handleOpenCertificateRegistration}
            >
              {isVerifying ? '프로그램 설치여부 확인중...' : '공동인증서 연동하기'}
            </Button>
          </Flex>
        ) : (
          <Button
            icon={downloadClicked ? null : <DownloadLine />}
            buttonSize="xlarge"
            buttonType="primary"
            disabled={isRegisterButtonDisabled}
            style={{
              width: '100%',
            }}
            loading={isVerifying}
            onClick={handleOpenCertificateRegistration}
          >
            {isVerifying ? '프로그램 설치여부 확인중...' : '공동인증서 연동하기'}
          </Button>
        )
      ) : (
        <Button
          icon={downloadClicked ? null : <DownloadLine />}
          buttonSize="xlarge"
          buttonType="primary"
          disabled={isRegisterButtonDisabled}
          style={{
            width: '100%',
          }}
          loading={isVerifying}
          onClick={() => {
            setDownloadClicked(true);
            downloadCodefProgram();
          }}
        >
          {isVerifying ? '프로그램 설치여부 확인중...' : '프로그램 다운로드'}
        </Button>
      )}
    </Flex>
  );
};

export default CodefDownloadSection;
